import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { AppNotice } from "../../components/common/AppNotice.js";
import { WorkspaceLoader } from "../../components/common/WorkspaceLoader.js";
import { Button } from "../../components/ui/button/index.js";
import { AnimatePresence, motion, useReducedMotion } from "../../lib/motion.js";
import { cn } from "../../lib/utils.js";
import { useMetaEmbeddedSignup } from "../whatsapp/use-meta-embedded-signup.js";
import { useFacebookPopup } from "../whatsapp/use-facebook-popup.js";
import {
  handoffWhatsAppSignup,
  WHATSAPP_SIGNUP_HANDOFF_EVENT,
} from "../whatsapp/whatsapp-signup-handoff.js";
import { useInstagramPopup } from "../instagram/use-instagram-popup.js";
import { FacebookPageSelection } from "../whatsapp/FacebookPage/FacebookPageSelection.js";
import {
  cancelPendingFacebook,
  selectFacebookPage,
} from "../../api/facebook-api.js";
import { ChannelConnectNotice } from "./ChannelConnectNotice.js";
import { ChannelConnectIntro } from "./ChannelConnectIntro.js";
import { ChannelModal } from "./ChannelModal.js";
import { channelModalStepMotion } from "./channel-modal-motion.js";
import { CHANNEL_CARD_ARTWORK, type ChannelCardArtworkId } from "./channel-artwork.js";
import {
  channelStatusLabelKey,
  isChannelAddOptionDisabled,
  isChannelConnected,
  type ChannelPlatformId,
} from "./channel-connection-policy.js";
import { useOrganizationPlatformFlags } from "../organizations/use-organization-platform-flags.js";
import { useChannelDirectory } from "./ChannelsWorkspaceLayout.js";

const ADD_CHANNEL_OPTIONS = [
  { id: "whatsapp", availability: "supported" },
  { id: "facebook", availability: "supported" },
  { id: "instagram", availability: "supported" },
  { id: "tiktok", availability: "soon" },
  { id: "x", availability: "soon" },
] as const;

type AddChannelOption = (typeof ADD_CHANNEL_OPTIONS)[number];

export function AddChannelDialog(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const directory = useChannelDirectory();
  const platformFlags = useOrganizationPlatformFlags(directory.organizationId);
  const meta = useMetaEmbeddedSignup();
  const whatsappLaunchLock = useRef(false);
  const [step, setStep] = useState<ChannelPlatformId | null>(null);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const reduceMotion = useReducedMotion() ?? false;
  const stepMotion = channelModalStepMotion(reduceMotion);
  const whatsappDisabled = platformFlags != null && !platformFlags.whatsappEnabled;
  const facebookDisabled = platformFlags != null && !platformFlags.facebookEnabled;
  const instagramDisabled = platformFlags != null && !platformFlags.instagramEnabled;
  const stepDisabled =
    step === "whatsapp"
      ? whatsappDisabled
      : step === "facebook"
        ? facebookDisabled
        : step === "instagram"
          ? instagramDisabled
          : false;

  useEffect(() => {
    if (!meta.launching) whatsappLaunchLock.current = false;
  }, [meta.launching]);

  useEffect(() => {
    if (!directory.addChannelOpen || step !== "whatsapp" || whatsappDisabled) return;
    if (directory.role !== "OWNER" && directory.role !== "ADMIN") return;
    const controller = new AbortController();
    void meta.loadConfig(directory.organizationId, controller.signal);
    return () => controller.abort();
  }, [
    directory.addChannelOpen,
    directory.organizationId,
    directory.role,
    meta.loadConfig,
    step,
    whatsappDisabled,
  ]);

  const facebookPopup = useFacebookPopup(() => {
    directory.reload();
  });
  const instagramPopup = useInstagramPopup(() => {
    directory.reload();
    directory.setAddChannelOpen(false);
  });

  const states: Record<ChannelPlatformId, string> = {
    whatsapp: directory.whatsapp?.state ?? "NOT_CONNECTED",
    facebook: directory.facebook?.state ?? "NOT_CONNECTED",
    instagram: directory.instagram?.state ?? "NOT_CONNECTED",
  };

  function close(): void {
    directory.setAddChannelOpen(false);
  }

  function selectPlatform(platform: ChannelPlatformId): void {
    if (isChannelAddOptionDisabled(states[platform])) return;
    setStep(platform);
  }

  const facebookPending =
    directory.facebook?.state === "PENDING_PAGE_SELECTION" &&
    (directory.facebook.pageCandidates?.length ?? 0) > 0;

  return (
    <ChannelModal
      open={directory.addChannelOpen}
      onOpenChange={(open) => {
        if (!open) close();
        else directory.setAddChannelOpen(true);
      }}
      title={
        step
          ? t(`channels.connect.${step}.title`)
          : t("channels.add.title")
      }
      description={
        step ? t(`channels.connect.${step}.description`) : t("channels.add.description")
      }
      testId="channels-add-dialog"
      className="max-h-[min(92dvh,46rem)] w-[min(42rem,calc(100vw-1.5rem))]"
      onExitComplete={() => {
        setStep(null);
        setSelectedPageId(null);
      }}
    >
      <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={step ?? "picker"}
        className="min-w-0"
        variants={stepMotion}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        {step === null ? (
          directory.loading ? (
            <WorkspaceLoader
              message={t("channels.home.loading")}
              layout="inline"
              testId="channels-add-loading"
            />
          ) : (
          <ul className="m-0 grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2" data-testid="channels-add-options">
            {ADD_CHANNEL_OPTIONS.map((option) => (
              <AddChannelOptionCard
                key={option.id}
                option={option}
                state={option.availability === "supported" ? states[option.id] : null}
                onSelect={option.availability === "supported" ? () => selectPlatform(option.id) : undefined}
              />
            ))}
          </ul>
          )
        ) : (
          <ChannelConnectIntro
            imageSrc={CHANNEL_CARD_ARTWORK[step]}
            title={t(`channels.connect.${step}.innerTitle`)}
            body={stepDisabled ? undefined : t(`channels.connect.${step}.body`)}
          >
            {stepDisabled ? (
              <AppNotice
                severity="error"
                description={t("channels.platformDisabled")}
                testId="channels-add-platform-disabled"
              />
            ) : null}
            {step === "facebook" && facebookPending && directory.facebook ? (
              <div className="w-full text-start">
                <FacebookPageSelection
                  candidates={directory.facebook.pageCandidates ?? []}
                  selectedPageId={selectedPageId}
                  canSelectPage={directory.facebook.permissions.canSelectPage}
                  canCancelPending={directory.facebook.permissions.canCancelPending}
                  busy={busy}
                  onSelectPage={setSelectedPageId}
                  onSubmitSelection={() => {
                    if (!selectedPageId) return;
                    setBusy(true);
                    void selectFacebookPage(directory.organizationId, selectedPageId)
                      .then(() => {
                        directory.reload();
                        close();
                      })
                      .finally(() => setBusy(false));
                  }}
                  onCancelPending={() => {
                    setBusy(true);
                    void cancelPendingFacebook(directory.organizationId)
                      .then(() => directory.reload())
                      .finally(() => setBusy(false));
                  }}
                />
              </div>
            ) : null}
            {step === "whatsapp" && !stepDisabled && meta.launchError ? (
              <AppNotice
                severity="error"
                description={meta.launchError}
                onDismiss={meta.clearLaunchError}
                dismissLabel={t("whatsapp.dismiss")}
                testId="channels-whatsapp-launch-error"
              />
            ) : null}
            {step === "whatsapp" && !stepDisabled && meta.configError ? (
              <AppNotice
                severity="error"
                description={
                  meta.configError === "FEATURE_DISABLED"
                    ? t("whatsapp.integrationUnavailable")
                    : meta.configError
                }
                testId="channels-whatsapp-config-error"
              />
            ) : null}
            {step === "facebook" && facebookPopup.status.kind === "error" ? (
              <ChannelConnectNotice
                platform="facebook"
                code={facebookPopup.status.code}
                onDismiss={facebookPopup.reset}
              />
            ) : null}
            {step === "instagram" && instagramPopup.status.kind === "error" ? (
              <ChannelConnectNotice
                platform="instagram"
                code={instagramPopup.status.code}
                onDismiss={instagramPopup.reset}
              />
            ) : null}
            {stepDisabled ? null : directory.role === "OWNER" || directory.role === "ADMIN" ? (
              step === "facebook" && facebookPending ? null : (
              <Button
                type="button"
                className="w-full max-w-xs"
                data-testid="channels-connect-submit"
                disabled={
                  (step === "facebook" && facebookPopup.status.kind === "opening") ||
                  (step === "instagram" && instagramPopup.status.kind === "opening") ||
                  (step === "whatsapp" && (meta.launching || meta.config?.enabled !== true))
                }
                onClick={() => {
                  if (step === "whatsapp") {
                    if (whatsappDisabled || whatsappLaunchLock.current || meta.config?.enabled !== true) return;
                    whatsappLaunchLock.current = true;
                    meta.clearLaunchError();
                    const organizationId = directory.organizationId;
                    meta.launch((result) => {
                      handoffWhatsAppSignup({ organizationId, result });
                      window.dispatchEvent(new Event(WHATSAPP_SIGNUP_HANDOFF_EVENT));
                      close();
                      if (location.pathname !== "/channels/whatsapp") {
                        navigate("/channels/whatsapp");
                      }
                    });
                    return;
                  }
                  if (step === "facebook") {
                    if (facebookDisabled) return;
                    facebookPopup.start(directory.organizationId);
                    return;
                  }
                  if (instagramDisabled) return;
                  instagramPopup.start(directory.organizationId);
                }}
              >
                {t(`channels.connect.${step}.action`)}
              </Button>
            )
            ) : (
              <p className="m-0 text-[0.8125rem] text-muted">{t("channels.connect.readOnly")}</p>
            )}
            {step === "facebook" && facebookPending ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => setStep(null)}>
                {t("channels.add.back")}
              </Button>
            ) : null}
          </ChannelConnectIntro>
        )}
      </motion.div>
      </AnimatePresence>
    </ChannelModal>
  );
}

function AddChannelOptionCard({
  option,
  state,
  onSelect,
}: {
  option: AddChannelOption;
  state: string | null;
  onSelect?: () => void;
}): React.JSX.Element {
  switch (option.availability) {
    case "supported":
      return <SupportedChannelCard platform={option.id} state={state ?? "NOT_CONNECTED"} onSelect={onSelect} />;
    case "soon":
      return <ComingSoonChannelCard id={option.id} />;
    default: {
      const unreachable: never = option;
      return unreachable;
    }
  }
}

function SupportedChannelCard({
  platform,
  state,
  onSelect,
}: {
  platform: ChannelPlatformId;
  state: string;
  onSelect?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const disabled = isChannelAddOptionDisabled(state);
  const connected = isChannelConnected(state);
  const detail =
    state === "NOT_CONNECTED" || state === "DISCONNECTED"
      ? t(`channels.add.cards.${platform}.setup`)
      : connected
        ? t("channels.status.connected")
        : t(channelStatusLabelKey(state));

  return (
    <li className="h-full min-w-0">
      <button
        type="button"
        disabled={disabled}
        data-testid={`channels-add-${platform}`}
        data-connected={connected ? "true" : "false"}
        className={channelCardClass(disabled)}
        onClick={() => onSelect?.()}
      >
        {disabled ? null : (
          <Plus
            size={14}
            aria-hidden="true"
            className="pointer-events-none absolute end-2 top-2 text-muted"
          />
        )}
        <ChannelCardArtwork id={platform} />
        <span className="text-[0.875rem] font-semibold text-foreground">
          {t(`channels.add.cards.${platform}.name`)}
        </span>
        <span className="text-[0.75rem] leading-snug text-muted">{detail}</span>
      </button>
    </li>
  );
}

function ComingSoonChannelCard({ id }: { id: "tiktok" | "x" }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <li className="h-full min-w-0">
      <button
        type="button"
        disabled
        data-testid={`channels-add-${id}`}
        className={channelCardClass(true)}
      >
        <ChannelCardArtwork id={id} />
        <span className="text-[0.875rem] font-semibold text-foreground">
          {t(`channels.add.cards.${id}.name`)}
        </span>
        <span className="inline-flex items-center rounded-full border border-line bg-surface-muted px-2 py-0.5 text-[0.6875rem] font-medium leading-none text-muted">
          {t("channels.add.comingSoon")}
        </span>
      </button>
    </li>
  );
}

function ChannelCardArtwork({ id }: { id: ChannelCardArtworkId }): React.JSX.Element {
  return (
    <img
      src={CHANNEL_CARD_ARTWORK[id]}
      alt=""
      width={400}
      height={400}
      className="h-[5.25rem] w-[5.25rem] object-contain"
    />
  );
}

function channelCardClass(disabled: boolean): string {
  return cn(
    "relative grid h-full w-full justify-items-center gap-2 rounded-lg border border-line bg-surface px-3 py-4 text-center",
    disabled
      ? "cursor-not-allowed disabled:opacity-100"
      : "cursor-pointer hover:border-line-strong hover:bg-surface-muted",
  );
}
